import {XIcon, CheckIcon, ChevronDownIcon} from '@primer/octicons-react'
import {Box, Button, Grid, Heading, Image, Label, SectionIntro, Stack, Text} from '@primer/react-brand'

interface PricingGroupedProps {
  copilotSignupPath: string
  copilotForBusinessSignupPath: string
  copilotContactSalesPath: string
}

interface Feature {
  title: string
  individual: boolean
  business: boolean
  enterprise: boolean
}

interface FeatureGroup {
  title: string
  features: Feature[]
}

// Set type as array of Feature
const chatFeatures: Feature[] = [
  {
    title: 'Unlimited messages, interactions, and history',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'Context-aware coding support and explanations',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'Debugging and security remediation assistance',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'Repository-based semantic search',
    individual: false,
    business: false,
    enterprise: true,
  },
  {
    title: 'Access your knowledge base',
    individual: false,
    business: false,
    enterprise: true,
  },
  {
    title: 'Pull request diff analysis',
    individual: false,
    business: false,
    enterprise: true,
  },
  {
    title: 'Bing integration (beta)',
    individual: false,
    business: false,
    enterprise: true,
  },
]

const codeCompletionFeatures: Feature[] = [
  {
    title: 'Code suggestions as you type',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'Comments to code',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'Fine-tuned models (coming soon)',
    individual: false,
    business: false,
    enterprise: true,
  },
]

const smartActionsFeatures: Feature[] = [
  {
    title: 'Inline chat and prompt suggestions',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'Slash commands and context variables',
    individual: false,
    business: true,
    enterprise: true,
  },
  {
    title: 'Pull request description and summarization',
    individual: false,
    business: false,
    enterprise: true,
  },
]

const devEnvFeatures: Feature[] = [
  {
    title: 'IDE, CLI and GitHub Mobile',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'Chat support for general coding questions',
    individual: false,
    business: false,
    enterprise: true,
  },
]

const managementPoliciesFeatures: Feature[] = [
  {
    title: 'IP indemnity',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'Public code filter',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'User/license management and SSO',
    individual: false,
    business: true,
    enterprise: true,
  },
  {
    title: 'Data excluded from training by default',
    individual: false,
    business: true,
    enterprise: true,
  },
  {
    title: 'Content exclusion',
    individual: false,
    business: true,
    enterprise: true,
  },
  {
    title: 'Requires GitHub Enterprise Cloud',
    individual: false,
    business: false,
    enterprise: true,
  },
]

const allFeatures: FeatureGroup[] = [
  {
    title: 'Chat',
    features: chatFeatures,
  },
  {
    title: 'Code completion',
    features: codeCompletionFeatures,
  },
  {
    title: 'Smart actions',
    features: smartActionsFeatures,
  },
  {
    title: 'Dev environments',
    features: devEnvFeatures,
  },
  {
    title: 'Management and policies',
    features: managementPoliciesFeatures,
  },
]

export function PricingGrouped({
  copilotSignupPath,
  copilotForBusinessSignupPath,
  copilotContactSalesPath,
}: PricingGroupedProps) {
  return (
    <div>
      <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap">
        <Grid.Column span={12}>
          <SectionIntro fullWidth className="lp-SectionIntro">
            <SectionIntro.Label size="large" className="lp-Label--section">
              Pricing
            </SectionIntro.Label>
            <SectionIntro.Heading size="1" weight="bold">
              Take flight with GitHub Copilot.
            </SectionIntro.Heading>
            <SectionIntro.Description className="lp-SectionIntro-description--wide">
              Organizations and developers all over the world use GitHub Copilot to code faster, drive impact, and focus
              focus on doing what matters most: building great software.
            </SectionIntro.Description>
          </SectionIntro>
        </Grid.Column>

        <Grid.Column span={12} className="position-relative">
          <Image
            className="lp-Pricing-bgGlow m-auto top-n7 top-md-n9 left-0 right-0 position-absolute col-11 col-md-6 height-auto events-none"
            src="/images/modules/site/copilot/pricing-glow.webp"
            loading="lazy"
            width="50"
            height="62"
            alt=""
          />

          <Grid className="lp-Pricing">
            <Grid.Column span={12} className="lp-Pricing-item px-0 px-md-4 py-0 has-BlurredBg has-GradientBorder">
              <Stack direction={{narrow: 'vertical', regular: 'horizontal'}} gap={32} padding="none">
                {/* Copilot Individual */}
                <Stack padding="none" gap={24} style={{flex: '1'}} className="pt-4 pt-md-6 pb-1 pb-md-9 px-4 px-md-0">
                  <Box>
                    <Label size="medium" color="purple-red" className="d-none d-md-block" style={{opacity: 0}}>
                      &nbsp;
                    </Label>
                    <Box marginBlockStart={16} marginBlockEnd={16}>
                      <Heading as="h3" size="5">
                        Copilot Individual
                      </Heading>
                    </Box>
                    <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description--org">
                      For individual developers, freelancers, students, and educators that want to code faster and
                      happier.
                    </Text>
                  </Box>

                  <Box>
                    <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                      <Text size="500" weight="normal" style={{lineHeight: 1.4}} className="is-sansSerifAlt">
                        $
                      </Text>
                      <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                        10
                      </Text>
                      <Text size="500" weight="normal" className="is-sansSerifAlt" style={{alignSelf: 'end'}}>
                        USD
                      </Text>
                    </Stack>

                    <Text size="100" weight="normal" variant="muted" className="d-block mt-2">
                      per month / $100 USD per year
                    </Text>
                  </Box>

                  <Stack direction={{narrow: 'vertical', wide: 'horizontal'}} gap={12} padding="none">
                    <Button as="a" href={copilotSignupPath} block variant="primary">
                      Start a free trial
                    </Button>
                  </Stack>

                  <Text variant="muted" className="d-block lp-Pricing-disclaimer f6-mktg">
                    Free for verified students, teachers, and maintainers of popular open source projects.
                  </Text>

                  <hr className="lp-Pricing-divider" />
                  <input type="checkbox" className="d-none lp-Pricing-features-toggle" id="features-individual" />
                  <label
                    htmlFor="features-individual"
                    className="position-relative lp-Pricing-features-label mb-0 mb-md-3"
                  >
                    What&apos;s included
                    <div className="lp-Pricing-features-icon position-absolute top-0 right-0 bottom-0 d-md-none">
                      <ChevronDownIcon />
                    </div>
                  </label>
                  {allFeatures.map((group: FeatureGroup, i) => (
                    <Box key={i} className="lp-Pricing-features-box">
                      <Text size="200" weight="normal" className="lp-Pricing-feature-group-title">
                        {group.title}
                      </Text>
                      <ul className="lp-Pricing-list">
                        {group.features.map((feature: Feature, j) => (
                          <li key={j} className={feature['individual'] ? 'lp-Pricing-list-check' : 'lp-Pricing-list-x'}>
                            {feature['individual'] ? <CheckIcon /> : <XIcon />} {feature['title']}
                          </li>
                        ))}
                      </ul>
                    </Box>
                  ))}
                </Stack>

                <hr className="lp-Pricing-divider lp-Pricing-divider--grouped" />

                {/* Copilot Business */}
                <Stack padding="none" gap={24} style={{flex: '1'}} className="pt-4 pt-md-6 pb-1 pb-md-9 px-4 px-md-0">
                  <Box>
                    <Label size="medium" color="purple-red" className="label-purple-green">
                      Most popular
                    </Label>
                    <Box marginBlockStart={16} marginBlockEnd={16}>
                      <Heading as="h3" size="5">
                        Copilot Business
                      </Heading>
                    </Box>
                    <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description--org">
                      For organizations ready to improve engineering velocity, code quality, and developer experience.
                    </Text>
                  </Box>

                  <Box>
                    <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                      <Text size="500" weight="normal" style={{lineHeight: 1.4}} className="is-sansSerifAlt">
                        $
                      </Text>
                      <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                        19
                      </Text>
                      <Text size="500" weight="normal" className="is-sansSerifAlt" style={{alignSelf: 'end'}}>
                        USD
                      </Text>
                    </Stack>

                    <Text size="100" weight="normal" variant="muted" className="d-block mt-2">
                      per user / month
                    </Text>
                  </Box>

                  <Stack direction={{narrow: 'vertical', wide: 'horizontal'}} gap={12} padding="none">
                    <Button as="a" href={copilotForBusinessSignupPath} block variant="primary">
                      Buy now
                    </Button>
                  </Stack>

                  <Text variant="muted" className="d-none d-md-block lp-Pricing-disclaimer f6-mktg">
                    &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                    &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                    &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                    &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                    &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                    &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                    &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  </Text>

                  <hr className="lp-Pricing-divider" />
                  <input type="checkbox" className="d-none lp-Pricing-features-toggle" id="features-business" />
                  <label
                    htmlFor="features-business"
                    className="position-relative lp-Pricing-features-label mb-0 mb-md-3"
                  >
                    What&apos;s included
                    <div className="lp-Pricing-features-icon position-absolute top-0 right-0 bottom-0 d-md-none">
                      <ChevronDownIcon />
                    </div>
                  </label>
                  {allFeatures.map((group: FeatureGroup, i) => (
                    <Box key={i} className="lp-Pricing-features-box">
                      <Text size="200" weight="normal" className="lp-Pricing-feature-group-title">
                        {group.title}
                      </Text>
                      <ul className="lp-Pricing-list">
                        {group.features.map((feature: Feature, j) => (
                          <li key={j} className={feature['business'] ? 'lp-Pricing-list-check' : 'lp-Pricing-list-x'}>
                            {feature['business'] ? <CheckIcon /> : <XIcon />} {feature['title']}
                          </li>
                        ))}
                      </ul>
                    </Box>
                  ))}
                </Stack>

                <hr className="lp-Pricing-divider lp-Pricing-divider--grouped" />

                {/* Copilot Enterprise */}
                <Stack padding="none" gap={24} style={{flex: '1'}} className="pt-4 pt-md-6 pb-6 pb-md-9 px-4 px-md-0">
                  <Box>
                    <Label size="medium" color="purple-red" className="label-purple-green">
                      New
                    </Label>
                    <Box marginBlockStart={16} marginBlockEnd={16}>
                      <Heading as="h3" size="5">
                        Copilot Enterprise
                      </Heading>
                    </Box>
                    <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description--org">
                      For companies looking to customize GitHub Copilot to their organization and infuse AI across the
                      developer workflow.
                    </Text>
                  </Box>

                  <Box>
                    <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                      <Text size="500" weight="normal" style={{lineHeight: 1.4}} className="is-sansSerifAlt">
                        $
                      </Text>
                      <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                        39
                      </Text>
                      <Text size="500" weight="normal" className="is-sansSerifAlt" style={{alignSelf: 'end'}}>
                        USD
                      </Text>
                    </Stack>

                    <Text size="100" weight="normal" variant="muted" className="d-block mt-2">
                      per user / month
                    </Text>
                  </Box>

                  <Button as="a" href={copilotContactSalesPath} variant="primary">
                    Contact sales
                  </Button>

                  <Text variant="muted" className="d-none d-md-block lp-Pricing-disclaimer f6-mktg">
                    &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                    &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                    &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                    &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                    &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                    &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                    &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  </Text>

                  <hr className="lp-Pricing-divider" />
                  <input type="checkbox" className="d-none lp-Pricing-features-toggle" id="features-enterprise" />
                  <label
                    htmlFor="features-enterprise"
                    className="position-relative lp-Pricing-features-label mb-0 mb-md-3"
                  >
                    What&apos;s included
                    <div className="lp-Pricing-features-icon position-absolute top-0 right-0 bottom-0 d-md-none">
                      <ChevronDownIcon />
                    </div>
                  </label>
                  {allFeatures.map((group: FeatureGroup, i) => (
                    <Box key={i} className="lp-Pricing-features-box">
                      <Text size="200" weight="normal" className="lp-Pricing-feature-group-title">
                        {group.title}
                      </Text>
                      <ul className="lp-Pricing-list">
                        {group.features.map((feature: Feature, j) => (
                          <li key={j} className={feature['enterprise'] ? 'lp-Pricing-list-check' : 'lp-Pricing-list-x'}>
                            {feature['enterprise'] ? <CheckIcon /> : <XIcon />} {feature['title']}
                          </li>
                        ))}
                      </ul>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Grid.Column>
          </Grid>
        </Grid.Column>
      </Grid>

      <div className="lp-Pricing-bgCover">
        <Image
          className="lp-Pricing-bgCoverImage"
          src="/images/modules/site/copilot/pricing-cover.svg"
          loading="lazy"
          alt=""
        />
      </div>
    </div>
  )
}

try{ PricingGrouped.displayName ||= 'PricingGrouped' } catch {}